"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { ArrowLeft, ChevronLeft, ChevronRight, PlusCircle } from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { usePublicProfileStore } from "../store/usePublicProfileStore";
import { ReviewForm } from "../components/ReviewForm";

import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { StarRating } from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import { PublicHeaderDisplay } from "@/features/profile/public/components/PublicHeaderDisplay";
import { PublicLanguageSwitcher } from "@/features/profile/public/components/PublicLanguageSwitcher";
import PATHS from "@/routes/paths";
import ReviewLoader from "@/wrappers/GetProfileDataWrapper/ReviewsLoader";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export default function PublicReviewsPage() {
  const {
    profileData,
    reviewsData,
    isLoading,
    reviewPage: currentPage,
    setReviewPage: setCurrentPage,
  } = usePublicProfileStore();
  const { t } = useTranslation(["profile", "common"]);
  const { profileId, locale } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showReviewForm, setShowReviewForm] = useState(false);

  useEffect(() => {
    const shouldShowForm =
      searchParams.get("addReview") === "true" &&
      (profileData?.settings.can_current_user_leave_review ?? false);

    setShowReviewForm(shouldShowForm);

    if (shouldShowForm) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  }, [searchParams]);

  useEffect(() => {
    if (reviewsData) {
      setCurrentPage(reviewsData.meta.current_page);
    }
  }, [reviewsData]);

  if (isLoading) return <ReviewLoader />;
  if (!profileData || !profileId || !reviewsData) return null;

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAddReviewClick = () => {
    setShowReviewForm(true);
    setSearchParams({ addReview: "true" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelReview = () => {
    setShowReviewForm(false);
    searchParams.delete("addReview");
    setSearchParams(searchParams);
  };

  return (
    <ScrollArea className="h-[calc(100dvh_-_4rem)]">
      <title>{`${profileData.header_section.name} - ${t("reviewsSection.title")} - Ximly`}</title>
      <div className="bg-background flex min-h-dvh flex-col-reverse items-center justify-center gap-4 space-y-6 px-1 sm:px-8 xl:flex-row xl:items-start">
        <div className="w-full space-y-6 lg:w-[50rem]">
          <PublicHeaderDisplay />
          <div className="container mx-auto max-w-4xl">
            <div className="w-full space-y-4">
              <Card className="bg-foreground relative overflow-hidden border-none shadow-md">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <motion.div
                    animate={{ y: 0, opacity: 1 }}
                    className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
                    initial={{ y: -10, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex items-center gap-2">
                      <Button asChild size="sm" variant="outline">
                        <Link
                          className="flex items-center gap-2"
                          to={`${PATHS.PROFILES.TUTOR_PROFILE}/${profileId}${locale !== undefined ? `/${locale}` : ""}`}
                        >
                          <ArrowLeft className="h-4 w-4" />
                          <span>{t("reviewsSection.backToProfile")}</span>
                        </Link>
                      </Button>
                    </div>

                    <div className="flex w-full items-center justify-between gap-4 sm:w-auto sm:justify-end">
                      <div className="flex items-center gap-2">
                        <StarRating showText rating={reviewsData.stats.average_rating} size="md" />
                        <span className="text-muted-foreground text-sm">
                          ({t("reviewsSection.reviews", { count: reviewsData.stats.total_reviews })}
                          )
                        </span>
                      </div>

                      {!showReviewForm && profileData.settings.can_current_user_leave_review && (
                        <Button
                          className="flex items-center gap-1"
                          size="sm"
                          onClick={handleAddReviewClick}
                        >
                          <PlusCircle className="h-4 w-4" />
                          <span className="hidden sm:inline">{t("reviewsSection.addReview")}</span>
                        </Button>
                      )}
                      {!showReviewForm && !profileData.settings.can_current_user_leave_review && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <div className="bg-foreground/20 text-muted-foreground/60 flex items-center justify-center gap-1 gap-2 rounded-md border p-2 text-sm font-medium whitespace-nowrap">
                                <PlusCircle className="h-4 w-4" />
                                <span className="hidden sm:inline">
                                  {t("reviewsSection.addReview")}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>{t("reviewsSection.notAbleToReview")}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </motion.div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Review form at the top of the list */}
                  {showReviewForm && <ReviewForm onCancel={handleCancelReview} />}

                  {reviewsData.data.length > 0 ? (
                    reviewsData.data.map((review, index) => (
                      <motion.div
                        key={review.id}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-side-accent rounded-lg p-4"
                        initial={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                      >
                        <div className="flex items-start gap-4">
                          <Avatar className="h-12 w-12 border">
                            <AvatarImage alt={review.user.name} src={review.user.avatar} />
                            <AvatarFallback>{review.user.name.substring(0, 2)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 space-y-2">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                              <h3
                                className="font-semibold"
                                title={t("reviewsSection.reviewerName")}
                              >
                                {review.user.name}
                              </h3>
                              <div className="flex items-center gap-2">
                                <StarRating rating={review.rating} />
                                <span
                                  className="text-muted-foreground text-sm"
                                  title={t("reviewsSection.reviewDate")}
                                >
                                  {new Date(review.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            <p className="text-base" title={t("reviewsSection.reviewMessage")}>
                              {review.content}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <p className="text-muted-foreground py-4 text-center">
                      {t("reviewsSection.noReviews")}
                    </p>
                  )}

                  {reviewsData.meta.last_page > 1 && (
                    <div className="flex items-center justify-center gap-2 py-6">
                      <Button
                        disabled={!reviewsData.links.prev}
                        size="icon"
                        title={t("reviewsSection.pagination.previous")}
                        variant="outline"
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>

                      {reviewsData.meta.links.map((link, index) => {
                        // Skip prev/next links that are represented by the arrow buttons
                        if (link.label === "&laquo; Previous" || link.label === "Next &raquo;") {
                          return null;
                        }

                        // Try to parse the label as a number
                        const pageNumber = Number.parseInt(link.label);

                        return (
                          <Button
                            key={index}
                            size="sm"
                            title={`${t("reviewsSection.pagination.page")} ${link.label}`}
                            variant={link.active ? "default" : "outline"}
                            onClick={() => handlePageChange(pageNumber)}
                          >
                            {link.label}
                          </Button>
                        );
                      })}

                      <Button
                        disabled={!reviewsData.links.next}
                        size="icon"
                        title={t("reviewsSection.pagination.next")}
                        variant="outline"
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        <div className={"flex w-full flex-col gap-4 sm:min-w-[23rem] lg:w-[50rem] xl:w-fit"}>
          {profileData.available_locales && profileData.available_locales.length > 1 && (
            <PublicLanguageSwitcher isInReviews={true} />
          )}
        </div>
      </div>
    </ScrollArea>
  );
}
